import { useCallback, useState } from "react";

export interface UseFormActionResult {
  saving: boolean;
  error: string | null;
  message: string | null;
  clearFeedback: () => void;
  run: (action: () => Promise<string | undefined>) => Promise<boolean>;
}

/**
 * Shared hook for form submission state management.
 *
 * Encapsulates the common `saving` / `error` / `message` trio and the
 * try-catch-finally pattern used across 16+ form/action components.
 *
 * `run()` returns `true` on success and `false` on error so callers can
 * conditionally reset forms or perform follow-up actions.
 */
export function useFormAction(): UseFormActionResult {
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const clearFeedback = useCallback(() => {
    setActionError(null);
    setMessage(null);
  }, []);

  const run = useCallback(
    async (action: () => Promise<string | undefined>): Promise<boolean> => {
      setSaving(true);
      setActionError(null);
      setMessage(null);
      try {
        const result = await action();
        setMessage(result ?? null);
        return true;
      } catch (error) {
        setActionError(error instanceof Error ? error.message : String(error));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  return { clearFeedback, error: actionError, message, run, saving };
}
